/**
 * ConfirmDialog
 *
 * The one confirmation popup of the app: every "are you sure?" goes through it, so they all
 * look and behave the same. Built on the FormDialog shell (gradient header, navy title, the
 * themed footer buttons), sized down, with an icon beside the message.
 *
 * Two ways to use it:
 *
 *  - Declarative, when the caller already keeps the open state:
 *      <ConfirmDialog open={...} title={...} message={...} danger
 *                     onConfirm={...} onCancel={...} />
 *
 *  - Imperative, for a one-off question from an event handler (mounted once, by
 *    <ConfirmDialogHost /> in Body):
 *      confirmDialog({ title, message, danger: true, onConfirm: () => ... })
 */
import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Langs from "../../app/lang/langs";
import FormDialog, { FormButton } from "./FormDialog";

// Above every dialog a confirmation is opened from: MUI (1300), react-bootstrap (1055) and the
// mobile full-screen dialogs raised up to 2000. Below the progress bar and toasts (9998+).
const Z_INDEX = 2100;

/**
 * @param {boolean} props.open
 * @param {React.ReactNode} [props.title] - defaults to "Confirmation"
 * @param {React.ReactNode} [props.message]
 * @param {React.ReactNode} [props.children] - extra content under the message
 * @param {boolean} [props.danger] - a destructive action: red icon and a red confirm button
 * @param {React.ElementType} [props.icon] - replaces the default icon (trash when danger,
 *        info for a notice, question mark otherwise), e.g. for a refusal that is not a delete
 * @param {React.ReactNode} [props.confirmLabel] - defaults to "Delete" when danger, else "Confirm"
 * @param {React.ReactNode} [props.cancelLabel] - defaults to "Cancel"
 * @param {boolean} [props.hideCancel] - a notice with a single button
 * @param {string} [props.maxWidth] - "xs" by default; wider for a question that lists details
 * @param {boolean} [props.fullScreen] - on a phone, for a long question
 * @param {boolean} [props.loading] - the confirmed request is in flight
 * @param {Function} props.onConfirm
 * @param {Function} [props.onCancel] - the cancel button
 * @param {Function} [props.onClose] - the close icon, Escape or a backdrop click; defaults to
 *        onCancel. Kept apart for the few questions whose "cancel" button is itself an action.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  children,
  danger = false,
  icon,
  confirmLabel,
  cancelLabel,
  hideCancel = false,
  maxWidth = "xs",
  fullScreen = false,
  loading = false,
  onConfirm,
  onCancel,
  onClose,
}) {
  const dismiss = onClose || onCancel;
  const Icon = icon || (danger ? DeleteOutlineRoundedIcon : (hideCancel ? InfoOutlinedIcon : HelpOutlineRoundedIcon));
  const tone = danger
    ? { color: "#C62828", bg: "rgba(198, 40, 40, 0.08)" }
    : { color: "#FF9015", bg: "rgba(255, 144, 21, 0.12)" };

  return (
    <FormDialog
      open={open}
      onClose={loading ? undefined : dismiss}
      title={title || <Langs str="CONFIRMATION" />}
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      sx={{ zIndex: Z_INDEX }}
      disableEnforceFocus
      actions={
        <>
          {!hideCancel && (
            <FormButton variant="secondary" onClick={onCancel || dismiss} disabled={loading}>
              {cancelLabel || <Langs str="CANCEL" />}
            </FormButton>
          )}
          <FormButton
            variant={danger ? "dangerSolid" : "primary"}
            onClick={onConfirm}
            loading={loading}
            autoFocus
          >
            {confirmLabel || <Langs str={danger ? "Delete" : "CONFIRM"} />}
          </FormButton>
        </>
      }
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, py: 1 }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: tone.bg,
            color: tone.color,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1, pt: 1 }}>
          {message && (
            <Typography component="div" sx={{ color: "rgba(0, 40, 128, 0.8)", fontSize: "0.93rem", lineHeight: 1.6, whiteSpace: "pre-line" }}>
              {message}
            </Typography>
          )}
          {children}
        </Box>
      </Box>
    </FormDialog>
  );
}

let showConfirm = null;

/**
 * Ask a question through the mounted <ConfirmDialogHost />.
 * @param {object} options - the ConfirmDialog props, except open/loading, plus:
 * @param {Function} [options.onHide] - after the dialog closes, however it was closed
 */
export function confirmDialog(options) {
  if (showConfirm) showConfirm(options);
  else console.warn("confirmDialog: no <ConfirmDialogHost /> is mounted");
}

/** Renders what confirmDialog() asks. Mount once. */
export function ConfirmDialogHost() {
  const [options, setOptions] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const show = (next) => {
      setOptions(next);
      setOpen(true);
    };
    showConfirm = show;
    return () => {
      if (showConfirm === show) showConfirm = null;
    };
  }, []);

  if (!options) return null;

  // Closed first, then the callback runs, so a handler that opens another dialog is not
  // stacked under this one. The options stay set so the text does not vanish mid-fade.
  const closeThen = (callback) => () => {
    setOpen(false);
    if (callback) callback();
    if (options.onHide) options.onHide();
  };

  const { onConfirm, onCancel, onClose, onHide, ...rest } = options;
  return (
    <ConfirmDialog
      {...rest}
      open={open}
      onConfirm={closeThen(onConfirm)}
      onCancel={closeThen(onCancel)}
      onClose={closeThen(onClose)}
    />
  );
}
